import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Cable, ShieldCheck, Zap, Home, Store, Building2, Phone, MessageCircle,
  CheckCircle2, ArrowRight, Sparkles,
} from "lucide-react";

const IMG_HUPS = "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/iefel4bi_hups1.png";
const IMG_HUPS_BATTERIES = "https://customer-assets-jt897jd0.emergentagent.net/job_battery-finder-5/artifacts/3t8bhvgq_hups.jpg";

const MODEL_GROUPS = [
  {
    series: "HB Series · Home UPS",
    tagline: "For homes, small shops & single-room load. Pure sinewave.",
    icon: Home,
    models: [
      { code: "AAM-HU-HB00850AP", va: "850 VA" },
      { code: "AAM-HU-HB01000AP", va: "1000 VA" },
      { code: "AAM-HU-HB01200AP", va: "1200 VA" },
      { code: "AAM-HU-HB01300AP", va: "1300 VA" },
      { code: "AAM-HU-HB01600AP", va: "1600 VA" },
      { code: "AAM-HU-HB01850AP", va: "1850 VA" },
    ],
  },
  {
    series: "HBP Series · Commercial UPS",
    tagline: "For shops, offices, clinics, larger inductive loads.",
    icon: Store,
    models: [
      { code: "AAM-HU-HBP002500", va: "2500 VA" },
      { code: "AAM-HU-HBP003500", va: "3500 VA" },
      { code: "AAM-HU-HBP004000", va: "4000 VA" },
      { code: "AAM-HU-HBP006000", va: "6000 VA" },
    ],
  },
  {
    series: "HE Series · Economy Compact UPS",
    tagline: "Compact, value line for essential home backup.",
    icon: Building2,
    models: [
      { code: "AAM-HU-HE0000850", va: "850 VA" },
      { code: "AAM-HU-HE0001100", va: "1100 VA" },
      { code: "AAM-HU-HE0001300", va: "1300 VA" },
      { code: "AAM-HU-HE0001750", va: "1750 VA" },
    ],
  },
];

const BENEFITS = [
  { icon: ShieldCheck, title: "Pure Sinewave Output", text: "Runs sensitive electronics like TVs, laptops, mixers & fans without buzz or damage." },
  { icon: Zap, title: "Fast Charging Circuit", text: "Smart charging cuts downtime and extends the battery's usable life." },
  { icon: Sparkles, title: "Multi-Mode Operation", text: "UPS mode for computers and Home mode for regular loads — you choose." },
  { icon: CheckCircle2, title: "Overload & Short-Circuit Protection", text: "Built-in safety cut-offs protect your appliances and wiring." },
];

const FAQS = [
  {
    q: "What is a HUPS and how is it different from a normal inverter?",
    a: "HUPS stands for Home UPS. Unlike a basic inverter, an Amaron HUPS switches to backup power in milliseconds with pure sinewave output — so your computers, TVs and modern LED lights don't reset or flicker. It also has smarter charging that protects the connected battery.",
  },
  {
    q: "Which HUPS VA rating should I pick for my home in Sikar?",
    a: "As a rule of thumb: 850–1000 VA runs 2–4 fans + 4–6 LED lights + TV. 1200–1600 VA adds a fridge or extra room load. Above 1600 VA is for larger homes or shops with 8+ appliances. Our team helps you size correctly, free of cost.",
  },
  {
    q: "Does the Amaron HUPS come with a battery?",
    a: "The HUPS unit and the tall-tubular / SMF backup battery are sold separately, so you can mix and match. We stock the full Amaron tall-tubular Quanta and inverter battery range at Bharat Battery House, Sikar.",
  },
  {
    q: "What warranty do I get on Amaron HUPS?",
    a: "Amaron Pro HUPS units come with a standard manufacturer warranty (typically 24 months). We handle the entire warranty claim process on your behalf — you don't have to chase the brand.",
  },
  {
    q: "Do you install and deliver HUPS in Sikar?",
    a: "Yes. We provide doorstep delivery across Sikar and nearby areas, plus installation guidance so the unit is wired safely to your main load. Call +91 95493 90000 to schedule.",
  },
];

// JSON-LD schema injected via useEffect so it lives on this route only
const PRODUCT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Amaron HUPS / Home UPS Range — Bharat Battery House, Sikar",
  "description": "Complete Amaron Pro Hi-Backup UPS (HUPS) range — HB, HBP and HE series — available at Bharat Battery House, Sikar.",
  "itemListElement": MODEL_GROUPS.flatMap((g, gi) =>
    g.models.map((m, i) => ({
      "@type": "ListItem",
      "position": gi * 10 + i + 1,
      "item": {
        "@type": "Product",
        "name": `Amaron HUPS ${m.va} — ${m.code}`,
        "sku": m.code,
        "category": g.series,
        "brand": { "@type": "Brand", "name": "Amaron" },
        "description": `${g.series}. ${g.tagline}`,
        "offers": {
          "@type": "Offer",
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "LocalBusiness", "name": "Bharat Battery House", "@id": "https://amaronbatterystore.com/#business" },
        },
      },
    }))
  ),
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(f => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

export default function HupsPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Amaron HUPS in Sikar · Home UPS Batteries · Bharat Battery House";

    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      };
    };

    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      const prev = el ? el.getAttribute("href") : null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return () => { if (prev !== null) el.setAttribute("href", prev); };
    };

    const restore = [
      setMeta("description", "Buy Amaron HUPS / Home UPS in Sikar — HB, HBP and HE series (850 VA to 6000 VA). Pure sinewave, expert sizing, doorstep delivery and warranty support. Call +91 95493 90000."),
      setMeta("og:title", "Amaron HUPS in Sikar · Home UPS Batteries · Bharat Battery House", "property"),
      setMeta("og:description", "Full Amaron Pro Hi-Backup UPS range (850 VA – 6000 VA) with pure sinewave output. Authorised outlet in Sikar with expert sizing & warranty support.", "property"),
      setMeta("og:url", "https://amaronbatterystore.com/products/hups-batteries", "property"),
      setCanonical("https://amaronbatterystore.com/products/hups-batteries"),
    ];

    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.setAttribute("data-page", "hups");
    s1.text = JSON.stringify(PRODUCT_SCHEMA);
    document.head.appendChild(s1);

    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.setAttribute("data-page", "hups-faq");
    s2.text = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(s2);

    return () => {
      document.title = prevTitle;
      restore.forEach(fn => fn && fn());
      s1.remove();
      s2.remove();
    };
  }, []);

  return (
    <div className="bg-background" data-testid="hups-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-secondary text-xs font-bold uppercase tracking-[0.2em]">
              <Cable className="h-3.5 w-3.5" />
              Amaron Pro · HUPS · Home UPS
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.02]">
              Amaron <span className="text-secondary">HUPS</span> Batteries<br className="hidden sm:block" /> in Sikar.
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-xl">
              The complete Amaron Pro Hi-Backup UPS range — HB, HBP and HE series from 850&nbsp;VA to 6000&nbsp;VA — with pure sinewave output, fast charging and full warranty support. Delivered and set up in Sikar and nearby areas.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="tel:+919549390000" data-testid="hups-call-btn">
                <Button className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white font-semibold">
                  <Phone className="h-4 w-4 mr-2" /> Call +91 95493 90000
                </Button>
              </a>
              <Link to="/find-battery/request" data-testid="hups-request-quote-btn">
                <Button variant="outline" className="rounded-full border-black/15">
                  Request Quote <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <a
                href="https://wa.me/919530498888"
                target="_blank" rel="noreferrer"
                data-testid="hups-whatsapp-btn"
              >
                <Button variant="outline" className="rounded-full border-black/15">
                  <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-black/10 aspect-[4/5] bg-card">
              <img src={IMG_HUPS} alt="Amaron Pro HUPS Home UPS unit — available at Bharat Battery House, Sikar" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Why Amaron HUPS</div>
          <h2 className="font-display text-3xl md:text-4xl font-black mt-2 tracking-tighter">Built for Indian power conditions.</h2>
          <p className="mt-3 text-muted-foreground">Voltage fluctuation, long outages, mixed loads — Amaron Pro HUPS is engineered for exactly this.</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map(b => (
            <Card key={b.title} data-testid={`hups-benefit-${b.title.toLowerCase().replace(/\s+/g,'-')}`} className="p-5 rounded-2xl bg-card border-black/10">
              <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
                <b.icon className="h-5 w-5 text-secondary" />
              </div>
              <div className="mt-4 font-display font-bold text-lg">{b.title}</div>
              <p className="mt-1.5 text-sm text-muted-foreground">{b.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* MODELS */}
      <section id="hups-models" className="bg-card border-y border-black/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Full Model List</div>
              <h2 className="font-display text-3xl md:text-4xl font-black mt-2 tracking-tighter">14 Amaron HUPS models. In stock.</h2>
              <p className="mt-3 text-muted-foreground">From 850&nbsp;VA compact home units to 6000&nbsp;VA commercial UPS — pick by load, we'll help.</p>
            </div>
            <div className="hidden md:block">
              <img src={IMG_HUPS_BATTERIES} alt="Amaron HUPS batteries — tall tubular battery for Home UPS" className="h-24 rounded-xl object-cover border border-black/10" />
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {MODEL_GROUPS.map(g => (
              <Card key={g.series} data-testid={`hups-series-${g.series.split(' ')[0].toLowerCase()}`} className="p-6 rounded-2xl bg-white border-black/10">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
                    <g.icon className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-lg leading-tight">{g.series}</div>
                    <div className="text-[11px] text-muted-foreground">{g.models.length} models</div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground italic">{g.tagline}</p>
                <ul className="mt-4 space-y-1.5">
                  {g.models.map(m => (
                    <li key={m.code} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-primary/5 border border-primary/10">
                      <span className="font-mono text-[11.5px] font-semibold text-foreground/90 truncate">{m.code}</span>
                      <span className="text-[11px] font-bold text-secondary shrink-0">{m.va}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">HUPS FAQs</div>
        <h2 className="font-display text-3xl md:text-4xl font-black mt-2 tracking-tighter">People also ask</h2>
        <div className="mt-8 space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`hups-faq-${i}`} className="group rounded-2xl border border-black/10 bg-card p-5 open:bg-white transition-colors">
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                <span className="font-display font-bold text-base md:text-lg">{f.q}</span>
                <ArrowRight className="h-4 w-4 mt-1 text-secondary transition-transform group-open:rotate-90 shrink-0" />
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-card border-t border-black/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="font-display text-2xl md:text-3xl font-black tracking-tighter">Not sure which HUPS size fits your home?</h3>
            <p className="mt-2 text-muted-foreground text-sm md:text-base">Tell us your load (fans, lights, TV, fridge) and we'll recommend the right VA rating — free, no obligation.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/find-battery/request" data-testid="hups-cta-request">
              <Button className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white font-semibold">
                Get a HUPS Recommendation <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <a href="tel:+919549390000" data-testid="hups-cta-call">
              <Button variant="outline" className="rounded-full border-black/15">
                <Phone className="h-4 w-4 mr-2" /> Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
